import { z } from 'zod';
/**
 * What a client of the public API may do (ADR-012 §4): one scope per domain and kind of access.
 * A writing scope (`…:write`) is granted only with DPoP-bound tokens (RFC 9449, ADR-012 §4).
 * New scopes are added, never renamed.
 */
export const ApiScope = z
    .enum(['firm:read', 'firm:write', 'obligations:read', 'tasks:read', 'webhooks:manage'])
    .meta({ id: 'ApiScope' });
const WRITE_SCOPES = new Set(['firm:write', 'webhooks:manage']);
/** Scopes that change data: their tokens must be bound to the client's key (DPoP). */
export const isWriteScope = (scope) => WRITE_SCOPES.has(scope);
/** How the firm app and the developer portal name each scope. */
export const apiScopeLabel = {
    'firm:read': 'Basisdaten lesen',
    'firm:write': 'Basisdaten pflegen',
    'obligations:read': 'Pflichten lesen',
    'tasks:read': 'Aufgaben lesen',
    'webhooks:manage': 'Webhooks verwalten',
};
/** What each scope allows, in the words of the firm (firm app, developer portal). */
export const apiScopeHint = {
    'firm:read': 'Angaben der Kanzlei, Berufsträger, Standorte und Nachweislisten',
    'firm:write': 'Angaben der Kanzlei ändern, Berufsträger und Standorte erfassen – nur mit an den Schlüssel gebundenen Tokens (DPoP)',
    'obligations:read': 'Die zuletzt abgeleiteten Pflichten',
    'tasks:read': 'Offene und erledigte Aufgaben',
    'webhooks:manage': 'Webhooks der Kanzlei einrichten, beenden und ihr Zustellprotokoll lesen – nur mit an den Schlüssel gebundenen Tokens (DPoP)',
};
/** What each scope opens, for developers (API reference). */
export const apiScopeDescription = {
    'firm:read': 'Base data of the firm: facts, licensed professionals, sites, evidence lists.',
    'firm:write': 'State facts of the firm, add licensed professionals and sites. DPoP-bound tokens only (RFC 9449).',
    'obligations:read': "The firm's obligations as last assessed.",
    'tasks:read': "The firm's tasks.",
    'webhooks:manage': "Manage the firm's webhook subscriptions and read their delivery log. DPoP-bound tokens only (RFC 9449).",
};
/** A client of the public API that a firm has set up (ADR-012 §2); never its secret. */
export const ApiClient = z
    .object({
    id: z.uuid(),
    /** The OAuth client id to request tokens with. */
    clientId: z.string(),
    name: z.string(),
    scopes: z.array(ApiScope),
    createdAt: z.iso.datetime({ offset: true }),
    /** Set once the access was withdrawn; tokens stop working at that moment. */
    revokedAt: z.iso.datetime({ offset: true }).nullable(),
    /** The partner app this is the installation of (ADR-015); `null` for the firm's own. */
    partnerApp: z.object({ id: z.uuid(), name: z.string() }).nullable(),
})
    .meta({ id: 'ApiClient' });
export const ApiClientList = z.array(ApiClient).meta({ id: 'ApiClientList' });
export const ApiClientInput = z
    .object({
    name: z.string().trim().min(1).max(100),
    scopes: z.array(ApiScope).min(1).max(ApiScope.options.length),
})
    .meta({ id: 'ApiClientInput' });
/** The new client with its secret - shown this once, kept by Kanos never. */
export const ApiClientCreated = z
    .object({
    client: ApiClient,
    clientSecret: z.string(),
    /** Where the client requests its tokens (OAuth 2.0 client credentials). */
    tokenUrl: z.url(),
})
    .meta({ id: 'ApiClientCreated' });
export const ApiClientParams = z.object({ apiClientId: z.uuid() }).meta({ id: 'ApiClientParams' });
//# sourceMappingURL=api-clients.js.map